/**
 * web/src/lib/queryClient.ts
 * QueryClient configured for the Phase 5 dashboard.
 * - retry: false on 401 (session is gone — no point retrying)
 * - staleTime: 5_000 to dampen redundant refetches in StrictMode dev
 * - refetchOnWindowFocus: false (WS push handles freshness — D-2.1)
 * - Global 401 handler redirects to /login?expired=1 for both query and
 *   mutation cache events (CONTEXT.md "Integration points" line 147-148).
 *
 * The redirect is short-circuited when already on /login so a login-page
 * error can't bounce the browser into an infinite redirect loop
 * (T-05-02-06 mitigation).
 */
import { QueryClient } from '@tanstack/react-query';
import { ApiClientError } from './api';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (error instanceof ApiClientError && error.status === 401) return false;
        return failureCount < 2;
      },
      staleTime: 5_000,
      refetchOnWindowFocus: false,
    },
    mutations: { retry: false },
  },
});

/**
 * Navigates to /login?expired=1 unless already on /login (avoids redirect
 * loops). No-ops in non-browser envs so SSR / vitest jsdom doesn't crash on a
 * missing window. Shared by the 401 cache handlers and the WS 1008 path (F13).
 */
export function redirectToLogin(): void {
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    window.location.assign('/login?expired=1');
  }
}

/**
 * Routes a 401 ApiClientError to the login page (see redirectToLogin).
 */
function redirectIfUnauthorized(error: unknown): void {
  if (error instanceof ApiClientError && error.status === 401) {
    redirectToLogin();
  }
}

/**
 * Subscribes to the QueryCache and routes 401s to /login?expired=1.
 * Module-side-effect — runs once at import time. Idempotent because
 * the QueryClient instance is module-scoped and the subscribe handler
 * is added exactly once.
 */
queryClient.getQueryCache().subscribe((event) => {
  redirectIfUnauthorized(event.query.state.error as unknown);
});

/**
 * Mirror handler for the MutationCache so POST/PUT 401s also redirect.
 * Same idempotency contract as the QueryCache subscriber above.
 */
queryClient.getMutationCache().subscribe((event) => {
  redirectIfUnauthorized(event.mutation?.state.error as unknown);
});
